/**
 * Horizontal offset per stack level (px).
 * Each overlapping event shifted by this amount to the right.
 */
const OVERLAP_OFFSET_PX = 14;

/**
 * Fixed margin from the left and right edges of the column (px).
 */
const BASE_MARGIN_PX = 2;

const APPOINTMENT_PREVIEW_BASE_Z_INDEX = 1;

export interface OverlapLayoutParams {
  topPx: number;
  heightPx: number;
  /** Absolute left offset from the column edge (px) */
  leftPx: number;
  /** Absolute right offset from the column edge (px). Fixed so right border never escapes. */
  rightPx: number;
  /** Stack column index within the overlap group (0-based) */
  stackIndex: number;
  /** z-index in normal state */
  zIndex: number;
}

interface EventInput {
  id: string;
  startMinutes: number;
  endMinutes: number;
  topPx: number;
  heightPx: number;
}

const getSameStartOrderMap = (events: EventInput[]): Map<string, number> => {
  const sameStartOrderMap = new Map<string, number>();
  const eventsByStartMinutes = new Map<number, EventInput[]>();

  events.forEach((event) => {
    const sameStartEvents = eventsByStartMinutes.get(event.startMinutes) ?? [];

    sameStartEvents.push(event);
    eventsByStartMinutes.set(event.startMinutes, sameStartEvents);
  });

  eventsByStartMinutes.forEach((sameStartEvents) => {
    [...sameStartEvents]
      .sort((leftEvent, rightEvent) => {
        if (leftEvent.endMinutes !== rightEvent.endMinutes) {
          return rightEvent.endMinutes - leftEvent.endMinutes;
        }

        return leftEvent.id.localeCompare(rightEvent.id);
      })
      .forEach((event, index) => {
        sameStartOrderMap.set(event.id, index);
      });
  });

  return sameStartOrderMap;
};

const getAppointmentPreviewZIndex = ({
  event,
  sameStartOrderMap,
  zIndexStep,
}: {
  event: EventInput;
  sameStartOrderMap: Map<string, number>;
  zIndexStep: number;
}): number =>
  APPOINTMENT_PREVIEW_BASE_Z_INDEX +
  event.startMinutes * zIndexStep +
  (sameStartOrderMap.get(event.id) ?? 0);

/**
 * Two events overlap if their time ranges intersect strictly.
 * Touching borders (end of A == start of B) is NOT considered an overlap.
 */
const doOverlap = (a: EventInput, b: EventInput): boolean =>
  a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;

/**
 * Union-Find helpers to build connected overlap groups.
 * Events A and C belong to the same group even if they don't directly overlap,
 * as long as there is a chain A-B-C where each consecutive pair overlaps.
 */
const buildOverlapGroups = (events: EventInput[]): number[] => {
  const parent = events.map((_, i) => i);

  const find = (i: number): number => {
    if (parent[i] !== i) {
      parent[i] = find(parent[i]);
    }

    return parent[i];
  };

  const union = (i: number, j: number) => {
    parent[find(i)] = find(j);
  };

  for (let i = 0; i < events.length; i++) {
    for (let j = i + 1; j < events.length; j++) {
      if (doOverlap(events[i], events[j])) {
        union(i, j);
      }
    }
  }

  return events.map((_, i) => find(i));
};

/**
 * Greedy column assignment within a single overlap group.
 * Assigns each event to the earliest free "stack column" so the stack stays compact.
 *
 * Returns a map: eventId → stackIndex (0-based column index).
 */
const assignStackColumns = (groupEvents: EventInput[]): Map<string, number> => {
  const sorted = [...groupEvents].sort((a, b) => {
    if (a.startMinutes !== b.startMinutes) {
      return a.startMinutes - b.startMinutes;
    }

    // Longer events go first when start times are equal
    return b.endMinutes - a.endMinutes;
  });

  // columnEnds[i] = endMinutes of the last event assigned to column i
  const columnEnds: number[] = [];
  const result = new Map<string, number>();

  for (const event of sorted) {
    const freeCol = columnEnds.findIndex(
      (endMin) => endMin <= event.startMinutes
    );
    const col = freeCol === -1 ? columnEnds.length : freeCol;

    if (col === columnEnds.length) {
      columnEnds.push(event.endMinutes);
    } else {
      columnEnds[col] = event.endMinutes;
    }

    result.set(event.id, col);
  }

  return result;
};

/**
 * Calculates horizontal layout parameters for a list of events in one column.
 *
 * Non-overlapping events get full-width positioning.
 * Overlapping events get staggered left/right offsets so every event is
 * visible, right borders never escape the column, and later events appear
 * on top (higher z-index).
 */
export const calculateOverlapLayout = (
  events: EventInput[]
): Map<string, OverlapLayoutParams> => {
  const result = new Map<string, OverlapLayoutParams>();

  if (events.length === 0) {
    return result;
  }

  const groupIds = buildOverlapGroups(events);
  const sameStartOrderMap = getSameStartOrderMap(events);
  const zIndexStep = events.length + 1;

  // Group events by their root component id
  const groupsMap = new Map<number, EventInput[]>();

  events.forEach((event, i) => {
    const gid = groupIds[i];

    if (!groupsMap.has(gid)) {
      groupsMap.set(gid, []);
    }

    groupsMap.get(gid)!.push(event);
  });

  for (const groupEvents of groupsMap.values()) {
    const isSingleEvent = groupEvents.length === 1;

    if (isSingleEvent) {
      const [event] = groupEvents;

      result.set(event.id, {
        topPx: event.topPx,
        heightPx: event.heightPx,
        leftPx: BASE_MARGIN_PX,
        rightPx: BASE_MARGIN_PX,
        stackIndex: 0,
        zIndex: getAppointmentPreviewZIndex({
          event,
          sameStartOrderMap,
          zIndexStep,
        }),
      });

      continue;
    }

    const columnMap = assignStackColumns(groupEvents);

    for (const event of groupEvents) {
      const stackIndex = columnMap.get(event.id) ?? 0;

      result.set(event.id, {
        topPx: event.topPx,
        heightPx: event.heightPx,
        // Shift right by one step per stack level. Right edge stays fixed.
        leftPx: BASE_MARGIN_PX + stackIndex * OVERLAP_OFFSET_PX,
        rightPx: BASE_MARGIN_PX,
        stackIndex,
        zIndex: getAppointmentPreviewZIndex({
          event,
          sameStartOrderMap,
          zIndexStep,
        }),
      });
    }
  }

  return result;
};
